import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Confirm, DataTable, LanguageSelect, languageName, languageOptions, localeDirection, toast, useEnabledLanguages,
  type Column, type Destructive, type EnabledLanguage,
} from '@drbell/ui';
import { DischargeDialog, PendingTransfers } from './Transfers.tsx';
import { ResidentTimes } from './ResidentTimes.tsx';
import {
  api, ApiError, MANAGERS,
  type OrgDetail, type OrgSummary, type Resident,
} from '../api.ts';

/**
 * Residents - RX-ORG-02.
 *
 * Until now this screen could add a resident and nothing else. Nobody could
 * change a room number after a move, correct a language chosen wrongly at
 * admission, or remove somebody who had left — and a resident who has left
 * still occupies a contracted seat, so the home pays for them indefinitely
 * and cannot admit the person taking their bed.
 */
/**
 * The languages DrBell offers now (the admin's switch, docs/31). `?rtl=1`:
 * a resident may read Arabic, Hebrew, Persian or Urdu on their phone.
 */
const loadLanguages = () => api.get<{ locales: EnabledLanguage[] }>('/api/i18n/locales?rtl=1');

/**
 * When this home actually serves meals.
 *
 * Load-bearing, not cosmetic. An Indian prescription says `1-0-1 A/F` — one
 * after breakfast, one after dinner — so every meal-anchored dose is computed
 * from these four times. A home serving breakfast at 07:00 while DrBell
 * assumes 08:00 gives every resident their morning tablet an hour late, on
 * every medicine, for ever, and nothing on any screen would say why.
 *
 * The defaults are DrBell's, and they were the only values anything could
 * ever have, because nothing could set them.
 */
const DEFAULT_MEALS = {
  breakfast: '08:00',
  lunch: '13:00',
  dinner: '20:00',
  bedtime: '22:00',
} as const;

const MEAL_LABELS: Array<[keyof typeof DEFAULT_MEALS, string]> = [
  ['breakfast', 'Breakfast'],
  ['lunch', 'Lunch'],
  ['dinner', 'Dinner'],
  ['bedtime', 'Bedtime'],
];

const MODES = [
  ['elder', 'Elder — larger type, one card, two buttons'],
  ['standard', 'Standard'],
  ['kid', 'Kid — guardian confirms each dose'],
] as const;

export function Residents({
  org, detail, onChanged, onOpenMedicines, onOpenAppointments, onImport,
}: {
  org: OrgSummary;
  detail: OrgDetail;
  onChanged: () => void;
  /** Opens this resident's medicines — the reason the home is here at all. */
  onOpenMedicines: (resident: Resident) => void;
  onOpenAppointments: (resident: Resident) => void;
  onImport: () => void;
}) {
  const [rows, setRows] = useState<Resident[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Resident | null>(null);
  const [pending, setPending] = useState<Destructive | null>(null);
  const [discharging, setDischarging] = useState<Resident | null>(null);

  const canManage = MANAGERS.includes(detail.yourRole);
  const atLimit = org.seatLimit !== null && detail.seatsUsed >= org.seatLimit;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get<{ patients: Resident[] }>(`/api/orgs/${org.id}/patients`);
      setRows(d.patients);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { void load(); }, [load]);

  /*
   * Filtered here rather than on the server.
   *
   * A home's resident list is bounded by its seat count — hundreds, not
   * millions — and it is already loaded for the rota and the handover.
   * Adding a round-trip per keystroke would make the screen slower, not
   * faster. If a home ever has thousands of residents this moves server-side,
   * and the table is already built for it.
   */
  const filtered = rows?.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      r.displayName.toLowerCase().includes(q) ||
      (r.locationNote ?? '').toLowerCase().includes(q)
    );
  }) ?? null;

  const columns: Column<Resident>[] = [
    { key: 'displayName', header: 'Resident', cell: (r) => <span className="font-medium">{r.displayName}</span> },
    {
      key: 'locationNote',
      header: 'Room',
      cell: (r) => r.locationNote ?? <span className="text-(--muted)">—</span>,
    },
    {
      key: 'locale',
      header: 'Language',
      cell: (r) => <span lang={r.locale} dir={localeDirection(r.locale)}>{languageName(r.locale)}</span>,
    },
    {
      key: 'presentationMode',
      header: 'Screen',
      optional: true,
      cell: (r) => r.presentationMode,
    },
    { key: 'timezone', header: 'Timezone', optional: true, cell: (r) => r.timezone },
    ...(canManage
      ? [{
          key: 'actions',
          header: '',
          width: '28rem',
          cell: (r: Resident) => (
            <div className="flex justify-end gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); onOpenMedicines(r); }}
                className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-(--accent)"
              >
                Medicines
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenAppointments(r); }}
                className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-(--accent)"
              >
                Appointments
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-(--accent)"
              >
                Edit
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setDischarging(r); }}
                title="Hand the record back to a family member"
                className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-(--accent)"
              >
                Discharge
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPending({
                    title: `Remove ${r.displayName}?`,
                    /*
                     * The counts are not available on this screen without a
                     * second request per resident, so this says what KINDS of
                     * record go rather than inventing numbers. Saying "all
                     * associated data" would be the vague phrasing this
                     * component exists to replace; saying "34 doses" when it
                     * might be 3,400 would be worse.
                     */
                    body: `Their medications, schedules, every recorded dose, prescriptions and photographs are erased. This cannot be undone.`,
                    reassurance: `The seat is freed immediately, so somebody else can be admitted. Staff accounts are unaffected.`,
                    confirmLabel: 'Erase this resident',
                    typeToConfirm: r.displayName,
                    onConfirm: async () => {
                      await api.del(`/api/patients/${r.id}`);
                      toast.success(`${r.displayName} was removed`, 'A seat is free again.');
                      await load();
                      onChanged();
                    },
                  });
                }}
                className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-red-500"
              >
                Remove
              </button>
            </div>
          ),
        }]
      : []),
  ];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="mr-auto text-lg font-semibold">
          Residents ({rows?.length ?? 0}
          {org.seatLimit !== null && ` of ${org.seatLimit}`})
        </h2>
        {canManage && (
          <button
            onClick={onImport}
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Import a list
          </button>
        )}
        {canManage && (
          <button
            onClick={() => setAdding(true)}
            disabled={atLimit || org.status !== 'active'}
            title={
              atLimit
                ? 'Every contracted seat is in use'
                : org.status !== 'active'
                  ? 'This organisation is not active yet'
                  : undefined
            }
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            Add a resident
          </button>
        )}
      </div>

      {atLimit && (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
          All {org.seatLimit} contracted seats are in use. Removing a resident
          frees one immediately; raising the limit needs DrBell.
        </p>
      )}

      {/* Above the table: a transfer is a decision somebody is waiting on,
          not a row to scroll past. */}
      <PendingTransfers onChanged={onChanged} />

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        loading={loading}
        error={error}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search by name or room…"
        noun="residents"
        storageKey="org-residents"
        onRowClick={onOpenMedicines}
        emptyAction={
          canManage && !atLimit ? (
            <button
              onClick={() => setAdding(true)}
              className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
            >
              Add the first one
            </button>
          ) : undefined
        }
      />

      {(adding || editing) && (
        <ResidentDialog
          orgId={org.id}
          resident={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={async () => {
            setAdding(false);
            setEditing(null);
            await load();
            onChanged();
          }}
        />
      )}

      {discharging && (
        <DischargeDialog
          orgId={org.id}
          resident={discharging}
          onClose={() => setDischarging(null)}
          onDone={async () => { setDischarging(null); await load(); onChanged(); }}
        />
      )}

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </section>
  );
}

function ResidentDialog({
  orgId, resident, onClose, onSaved,
}: {
  orgId: string;
  resident: Resident | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [name, setName] = useState(resident?.displayName ?? '');
  const [room, setRoom] = useState(resident?.locationNote ?? '');
  const [locale, setLocale] = useState(resident?.locale ?? 'en-IN');
  const enabledLanguages = useEnabledLanguages(loadLanguages);
  const languages = useMemo(
    () => languageOptions(enabledLanguages, resident?.locale ?? 'en-IN'),
    [enabledLanguages, resident?.locale],
  );
  const [mode, setMode] = useState(resident?.presentationMode ?? 'elder');
  const [meals, setMeals] = useState<Record<string, string>>({ ...DEFAULT_MEALS });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Read back for an existing resident, so the form shows what is in force
   * rather than the defaults — otherwise saving any other field would quietly
   * reset a home's carefully-set meal times to 08:00.
   */
  useEffect(() => {
    if (!resident) return;
    void api
      .get<{ mealTimes: Record<string, string> | null }>(`/api/patients/${resident.id}`)
      .then((d) => { if (d.mealTimes) setMeals({ ...DEFAULT_MEALS, ...d.mealTimes }); })
      .catch(() => { /* keep the defaults; the field still saves */ });
  }, [resident]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (resident) {
        await api.put(`/api/patients/${resident.id}`, {
          displayName: name.trim(),
          locationNote: room.trim() || null,
          locale,
          presentationMode: mode,
        });
        // Its own endpoint, because changing a meal time moves every
        // meal-anchored dose and the server rebuilds them.
        await api.put(`/api/patients/${resident.id}/meal-times`, meals);
      } else {
        await api.post('/api/patients', {
          displayName: name.trim(),
          orgId,
          locationNote: room.trim() || undefined,
          locale,
          presentationMode: mode,
          mealTimes: meals,
        });
      }
      toast.success(resident ? `${name.trim()} updated` : `${name.trim()} added`);
      await onSaved();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 402
          ? 'Every contracted seat is in use. Remove a resident or ask DrBell to raise the limit.'
          : e instanceof ApiError ? e.message : String(e),
      );
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="resident-dialog-title">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="resident-dialog-title" className="text-lg font-semibold">
          {resident ? `Edit ${resident.displayName}` : 'New resident'}
        </h2>

        <label className="mt-4 block">
          <span className="text-xs text-(--muted)">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          />
        </label>

        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">Room or bed — optional</span>
          <input
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            placeholder="2B"
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          />
          {/* Rooms are sorted numerically in the handover, so a nurse walks
              the corridor once. Worth saying, because a home that types
              "Room 2B" everywhere gets a different order than one that
              types "2B". */}
          <span className="mt-1 block text-xs text-(--muted)">
            Used to order the shift handover, so keep it short and consistent.
          </span>
        </label>

        <div className="mt-3 block">
          <span className="text-xs text-(--muted)">Language</span>
          <LanguageSelect className="mt-1" value={locale} options={languages} onChange={setLocale} />
        </div>

        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">Screen</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as Resident['presentationMode'])}
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          >
            {MODES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        </label>

        <fieldset className="mt-4 rounded-lg border border-(--line) p-3">
          <legend className="px-1 text-xs text-(--muted)">
            When this home serves meals
          </legend>
          <div className="grid grid-cols-2 gap-3">
            {MEAL_LABELS.map(([key, label]) => (
              <label key={key} className="block">
                <span className="text-xs text-(--muted)">{label}</span>
                <input
                  type="time"
                  value={meals[key] ?? DEFAULT_MEALS[key]}
                  onChange={(e) => setMeals((m) => ({ ...m, [key]: e.target.value }))}
                  className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
                />
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-(--muted)">
            A prescription that reads <code>1-0-1 after food</code> is timed
            from these. Get them right once and every meal-anchored medicine
            lands where it should.
          </p>
        </fieldset>

        {/* Their own times of day besides the meals (owner, 4 Oct 2026): saved as they change. */}
        {resident && <ResidentTimes residentId={resident.id} />}

        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
            Cancel
          </button>
          <button
            disabled={busy || name.trim().length < 2}
            onClick={() => void save()}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Saving…' : resident ? 'Save changes' : 'Add resident'}
          </button>
        </div>
      </div>
    </div>
  );
}
